import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { ComponentsModule } from '@shared/components/components.module';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule, NgForOf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PipesModule } from '@shared/pipes/pipes.module';
import { DataRef, Domain, Language, Market } from '@shared/models/dataref.model';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MatDividerModule } from '@angular/material/divider';
import { FilteredData } from '@recipes/recipes-search/recipes-search-query/recipe-headband/recipe-headband.component';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';

export interface RecipesCopyByMarket {
  domain: Domain;
  market: Market;
  lang: Language;
  filteredData: FilteredData;
  languages: Language[];
  statuses: DataRef[];
}

@Component({
  selector: 'app-copy-by-market-popin',
  templateUrl: './copy-by-market-popin.component.html',
  styleUrls: ['./copy-by-market-popin.component.scss'],
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    ComponentsModule,
    TranslateModule,
    MatSelectModule,
    NgForOf,
    ReactiveFormsModule,
    PipesModule,
    MatDividerModule,
    MatCardModule,
    MatInputModule,
    CommonModule,
    MatCheckboxModule,
  ],
})
export class CopyByMarketPopinComponent implements OnInit, OnDestroy {
  popinForm: FormGroup;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CopyByMarketPopinComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RecipesCopyByMarket,
    private readonly formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.popinForm = this.formBuilder.group({
      ids: [[]],
      sourceDomain: [this.data.domain.key],
      sourceMarket: [this.data.market.key],
      sourceLang: [this.data.lang.key],
      sourceStatus: [null, Validators.required],
      targetDomain: [this.data.domain.key],
      targetMarket: [null, Validators.required],
      targetLang: [null, Validators.required],
      translationRequested: [false],
    });
    this.popinForm.get('targetLang')?.disable();
    this.popinForm.controls.translationRequested.disable();

    this.popinForm
      .get('targetMarket')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        switchMap((value: Market) => {
          if (value) {
            return this.handleMarketAction(value).pipe(tap(() => this.popinForm.get('targetLang')?.enable()));
          } else {
            return of(null).pipe(tap(() => this.popinForm.get('targetLang')?.disable()));
          }
        }),
      )
      .subscribe();

    //Handle recipe translation
    this.popinForm
      .get('targetLang')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap((targetLang: Language | null) => {
          //Reset checkbox value when targetLang is changed
          this.popinForm.controls.translationRequested.setValue(false);
          //Fetch from back if translation is available
          if (targetLang) {
            this.popinForm.controls.translationRequested.enable();
          } else {
            this.popinForm.controls.translationRequested.disable();
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.popinForm.invalid) {
      return;
    }
    const targetMarketValue = this.popinForm.get('targetMarket')?.value?.key;
    const targetLangValue = this.popinForm.get('targetLang')?.value?.key;
    this.popinForm.patchValue({
      targetMarket: targetMarketValue,
      targetLang: targetLangValue,
    });
    this.dialogRef.close(this.popinForm.value);
  }

  handleMarketAction(market?: Market): Observable<void> {
    return of(void 0).pipe(
      tap(() => {
        if (market?.langs) {
          const marketLangKeys = (market.langs as Array<Language>).map((lang: Language) => lang.key);
          this.data.filteredData.filteredLanguages = this.data.languages.filter((lang: { key: string }) =>
            marketLangKeys.includes(lang.key),
          );
          if (this.data.filteredData.filteredLanguages.length === 1) {
            this.popinForm.patchValue({
              targetLang: this.data.filteredData.filteredLanguages[0],
            });
          } else {
            this.popinForm.patchValue({
              targetLang: null,
            });
          }
        } else {
          this.data.filteredData.filteredLanguages = [];
          this.popinForm.patchValue({
            targetLang: this.data.filteredData.filteredLanguages[0],
          });
        }
      }),
    );
  }
}
